import React, { useState, useEffect } from "react";
import NavBar from "../NavBar";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Card,
  CardActions,
  CardContent,
  CircularProgress
} from "@mui/material";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import "./accountStyle.css";
import axios from "axios";
import EditUser from "../modals/EditUser";
import EditClient from "../modals/EditClient";
import { useAuth } from "../auth/AuthProvider";
import { getStandardInputLabelProps } from '../../utils/muiTextFieldProps';

const PlanLookup = {
  "0": { title: "No Modules", Description: "No modules selected", priceId: null },
  "1": { title: "Map System", Description: "Interactive mapping and asset management", priceId: "price_1PlWWlRsqwelbBw0knrR97lH" },
  "2": { title: "Call Module", Description: "Call handling and management system", priceId: "price_1PlZVARsqwelbBw0RY1EZruu" },
  "3": { title: "Both Modules", Description: "Map system and call module", priceId: "price_1RbKqHRsqwelbBw0sUy26W4v" },
};

function Account() {
  const { user } = useAuth(); // Get user from AuthProvider 
  // -----------------------------
  //  STATE
  // -----------------------------
  const [people, setPeople] = useState([]);
  const [ClientInfo, setClientInfo] = useState(null);
  const [userInfo, setUserInfo] = useState(null);

  // Display toggles
  const [showUserInfo, setShowUserInfo] = useState(true);
  const [showClientInfo, setShowClientInfo] = useState(true);
  const [showUserTable, setShowUserTable] = useState(true);
  const [showChangePlan, setShowChangePlan] = useState(false);
  const [InvitingUser, setInvitingUser] = useState(false);

  // Loading & Errors
  const [isLoadingUser, setIsLoadingUser] = useState(false);
  const [changePlanError, setChangePlanError] = useState(null);
  const [isLoadingChangingPlan, setIsLoadingChangingPlan] = useState(false);
  const [inviteError, setInviteError] = useState(null);

  // Editing / Modals
  const [editUser, setEditUser] = useState(false);
  const [editClient, setEditClient] = useState(false);
  const [passwordReset, setPasswordReset] = useState(false);

  // Plan
  const [currentPlan, setCurrentPlan] = useState("1");

  // For invite form fields
  const [inviteForm, setInviteForm] = useState({
    first: "",
    last: "",
    Email: "",
    Title: "",
    RoleID: "",
    UserLevel: "",
  });

  // For new data about user
  const [passwordError, setPasswordError] = useState(null);

  // -----------------------------
  //  EFFECT: On mount, fetch data
  // -----------------------------
  useEffect(() => {
    if (!user) {
      window.location = "/login";
      return;
    }

    const fetchAccountData = async () => {
      try {
        const { data } = await axios.get(`/api/accountsPage/${user.ClientID}`);
        if (data.message === "No token provided") {
          window.location = "/login";
          return;
        }
        const { userInfo, ClientInfo, allUsers } = data;
        setPeople(allUsers);
        setClientInfo(ClientInfo);
        setUserInfo(userInfo);

        // e.g. if plan is "1", that means "Small"
        const planId = ClientInfo[0].ClientPlanID.toString();
        setCurrentPlan(planId);
      } catch (err) {
        console.error("Error fetching account data:", err);
      }
    };
    fetchAccountData();
  }, [user]);

  // -----------------------------
  //  HANDLERS
  // -----------------------------
  const changeValue = (e) => {
    e.preventDefault();
    const { name, value } = e.target;
    setInviteForm((prev) => ({ ...prev, [name]: value }));
  };

  const submitForm = async () => {
    const body = {
      FirstName: inviteForm.first,
      LastName: inviteForm.last,
      Title: inviteForm.Title,
      Email: inviteForm.Email,
      RoleID: inviteForm.RoleID,
      UserLevel: inviteForm.UserLevel,
      clientID: ClientInfo[0].ClientID,
      ClientName: ClientInfo[0].ClientName,
    };
    try {
      const { data } = await axios.post(`/api/inviteUser`, body);
      if (data.message === "No token provided") {
        window.location = "/login";
        return;
      }
      setPeople(data.allUsers);
      setInvitingUser(false);
      setInviteError(null);
    } catch (err) {
      console.error("Invite error:", err);
      setInviteError(err.toString());
    }
  };

  const submitEditUserForm = async (form) => {
    // form is the user form from <EditUser />
    // e.g. { FirstName, LastName, Title, Email, RoleID, UserLevel }
    
    // Security check: Only admins can change roles
    if (form.RoleID !== userInfo[0].RoleID && user.RoleID !== 1) {
      setInviteError("Only administrators can modify user roles");
      return;
    }
    
    const body = {
      FirstName: form.FirstName,
      LastName: form.LastName,
      Title: form.Title,
      Email: form.Email,
      RoleID: form.RoleID,
      UserLevel: form.UserLevel,
      // Possibly userID from user
      UserID: user.UserID,
      ClientID: ClientInfo[0].ClientID,
    };
    try {
      const { data } = await axios.post(`/api/editUser`, body);
      if (data.message === "No token provided") {
        window.location = "/login";
        return;
      }
      setUserInfo(data.userInfo);
      setEditUser(false);
      setInviteError(null);
    } catch (err) {
      console.error("Edit user error:", err);
      setInviteError(err.toString());
    }
  };

  const submitEditClientForm = async (form) => {
    // form = client form from <EditClient />
    const body = {
      ClientName: form.ClientName,
      AddressLine1: form.AddressLine1,
      City: form.City,
      StateCode: form.StateCode,
      ZipCode: form.ZipCode,
      ClientContactFirstName: form.ClientContactFirstName,
      ClientContactLastName: form.ClientContactLastName,
      ClientContactEmailAddress: form.ClientContactEmailAddress,
      ClientID: ClientInfo[0].ClientID,
    };
    try {
      const { data } = await axios.post(`/api/editClient`, body);
      if (data.message === "No token provided") {
        window.location = "/login";
        return;
      }
      setClientInfo(data.clientInfo);
      setEditClient(false);
      setInviteError(null);
    } catch (err) {
      console.error("Edit client error:", err);
      setInviteError(err.toString());
    }
  };

  // resets the user's password
  const resetPassword = async () => {
    const body = { Email: user.email };
    try {
      const { data } = await axios.post(`/api/resetPassword`, body);
      if (data.message === "No token provided") {
        window.location = "/login";
        return;
      }
      // log them out
      await axios.get("/logout");
      window.location = "/login";
    } catch (err) {
      console.error("resetPassword error:", err);
      setPasswordError(err.toString());
    }
  };

  const closeModals = () => {
    setEditUser(false);
    setEditClient(false);
    setPasswordReset(false);
  };

  const deleteUser = async (oneUser) => {
    setIsLoadingUser(true);
    const body = {
      uid: oneUser.LoginID,
      UserID: oneUser.UserID,
      ClientID: oneUser.ClientID,
    };
    try {
      await axios.post(`/api/deleteUser`, body);
      window.location.reload();
    } catch (err) {
      console.error("deleteUser error:", err);
      setIsLoadingUser(false);
    }
  };

  const changePlan = async (planId) => {
    setIsLoadingChangingPlan(true);
    setChangePlanError(null);

    const body = {
      ClientInfo: ClientInfo[0],
      plan: PlanLookup[planId].priceId,
      planId: parseInt(planId, 10),
    };
    try {
      const { data } = await axios.post(`/api/changePlan`, body);
      if (data.message === "No token provided") {
        window.location = "/login";
        return;
      }
      window.location.reload();
    } catch (err) {
      console.error("changePlan error:", err);
      setIsLoadingChangingPlan(false);
      setChangePlanError(err.toString());
    }
  };

  // ------------
  // RENDER
  // ------------
  if (!user) {
    return null; // or some fallback
  }

  return (
    <div style={{ overflowX: "hidden", height: "100vh", background: "white" }}>
      {/* Modals */}
      {editUser && (
        <EditUser
          user={{
            FirstName: userInfo[0].FirstName,
            LastName: userInfo[0].LastName,
            Email: user.email,
            Department: userInfo[0].Department,
            Title: userInfo[0].Title,
            RoleID: userInfo[0].RoleID,
            UserLevel: userInfo[0].UserLevel,
          }}
          currentUser={user}
          submitForm={submitEditUserForm}
          onClose={closeModals}
        />
      )}

      {editClient && (
        <EditClient
          client={{
            ClientName: ClientInfo[0].ClientName,
            AddressLine1: ClientInfo[0].AddressLine1,
            City: ClientInfo[0].City,
            StateCode: ClientInfo[0].StateCode,
            ZipCode: ClientInfo[0].ZipCode,
            ClientContactFirstName: ClientInfo[0].ClientContactFirstName,
            ClientContactLastName: ClientInfo[0].ClientContactLastName,
            ClientContactEmailAddress: ClientInfo[0].ClientContactEmailAddress,
          }}
          submitForm={submitEditClientForm}
          onClose={closeModals}
        />
      )}

      {passwordReset && (
        <div className="modalStyle">
          <div className="innermodal">
            <Card className="cardStyle" style={{ width: "295px" }}>
              <CardContent>
                <p style={{ textAlign: "center" }}>
                  An email will be sent to your email address. Follow the link to reset your
                  password. This will also log you out.
                </p>
                <p style={{ fontWeight: 800, paddingTop: "31px", textAlign: "center" }}>
                  Are you sure you want to proceed?
                </p>
                {passwordError && (
                  <p style={{ color: "red" }}>Reset Failed. Try again later.</p>
                )}
              </CardContent>
              <CardActions style={{ justifyContent: "space-evenly" }}>
                <Button variant="contained" color="primary" onClick={resetPassword}>
                  Yes
                </Button>
                <Button variant="contained" color="secondary" onClick={() => setPasswordReset(false)}>
                  No
                </Button>
              </CardActions>
            </Card>
          </div>
        </div>
      )}

      <NavBar isAccountScreen={true} />

      <div style={{ padding: "60px 30px 30px 30px" }}>
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <h1 style={{ borderBottom: "1px solid black", width: "100%" }}>Account</h1>
        </div>

        {/* User Info */}
        <div style={{ maxWidth: "800px", margin: "auto" }}>
          <div style={{ paddingBottom: "20px" }}>
            <h2
              className="userCard"
              role="button"
              tabIndex={0}
              onClick={() => setShowUserInfo((prev) => !prev)}
            >
              User Info
            </h2>
            {showUserInfo && (
              <Card className="cardStyle">
                {userInfo ? (
                  <div>
                    <CardContent>
                      <div>
                        <div>
                          First Name: <b className="bolder">{userInfo[0].FirstName}</b>
                        </div>
                        <div>
                          Last Name: <b className="bolder">{userInfo[0].LastName}</b>
                        </div>
                        <div>
                          Email: <b className="bolder">{user.email}</b>
                        </div>
                        <div>
                          Department: <b className="bolder">{userInfo[0].Department}</b>
                        </div>
                        <div>
                          Title: <b className="bolder">{userInfo[0].Title}</b>
                        </div>
                        <div>
                          RoleID:{" "}
                          <b className="bolder">{userInfo[0].RoleID === 1 ? "Admin" : "User"}</b>
                        </div>
                        <div style={{ margin: "24px 0px" }}></div>
                      </div>
                    </CardContent>
                    <CardActions style={{ justifyContent: "space-between" }}>
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={() => setEditUser(true)}
                      >
                        {user.RoleID === 1 ? "Edit User" : "Edit Profile"}
                      </Button>
                      <Button
                        variant="contained"
                        color="secondary"
                        onClick={() => setPasswordReset(true)}
                      >
                        Change Password
                      </Button>
                    </CardActions>
                  </div>
                ) : (
                  <div style={{ textAlign: "center", padding: "50px" }}>
                    <CircularProgress color="secondary" className="loadingCircle" />
                  </div>
                )}
              </Card>
            )}
          </div>

          {/* Organization Info or Change Plan */}
          {showChangePlan ? (
            <div style={{ paddingBottom: "20px" }}>
              <h2 className="userCard">Changing Plan</h2>
              <Card className="cardStyle">
                {user.RoleID === 1 && ClientInfo ? (
                  <div>
                    <CardContent>
                      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "20px", flexWrap: "wrap", gap: "20px" }}>
                        {/* Map System */}
                        <div
                          className="changePlan"
                          style={currentPlan === "1" ? { opacity: "33%" } : {}}
                        >
                          <h3>Map System</h3>
                          <p>
                            <b>$1000 / year</b>
                          </p>
                          <p>Interactive mapping and asset management</p>
                          {currentPlan === "1" ? (
                            <p>(Current Module)</p>
                          ) : (
                            <Button
                              style={{ transform: "translate(50%, 75%)" }}
                              variant="contained"
                              color="secondary"
                              onClick={() => changePlan("1")}
                              disabled={isLoadingChangingPlan || currentPlan === "1"}
                            >
                              {isLoadingChangingPlan ? "Loading..." : "Select"}
                            </Button>
                          )}
                        </div>
                        {/* Call Module */}
                        <div
                          className="changePlan"
                          style={currentPlan === "2" ? { opacity: "33%" } : {}}
                        >
                          <h3>Call Module</h3>
                          <p>
                            <b>$1000 / year</b>
                          </p>
                          <p>Call handling and management system</p>
                          {currentPlan === "2" ? (
                            <p>(Current Module)</p>
                          ) : (
                            <Button
                              style={{ transform: "translate(50%, 75%)" }}
                              variant="contained"
                              color="secondary"
                              onClick={() => changePlan("2")}
                              disabled={isLoadingChangingPlan || currentPlan === "2"}
                            >
                              {isLoadingChangingPlan ? "Loading..." : "Select"}
                            </Button>
                          )}
                        </div>
                        {/* Both Modules */}
                        <div
                          className="changePlan"
                          style={currentPlan === "3" ? { opacity: "33%" } : {}}
                        >
                          <h3>Both Modules</h3>
                          <p>
                            <b>$1800 / year</b>
                          </p>
                          <p>Map system and call module</p>
                          {currentPlan === "3" ? (
                            <p>(Current Module)</p>
                          ) : (
                            <Button
                              style={{ transform: "translate(50%, 75%)" }}
                              variant="contained"
                              color="secondary"
                              onClick={() => changePlan("3")}
                              disabled={isLoadingChangingPlan || currentPlan === "3"}
                            >
                              {isLoadingChangingPlan ? "Loading..." : "Select"}
                            </Button>
                          )}
                        </div>
                      </div>
                      {changePlanError && (
                        <p style={{ color: "red", textAlign: "center" }}>
                          Changing Plan Failed. Try again Later
                        </p>
                      )}
                    </CardContent>
                    <CardActions style={{ justifyContent: "center" }}>
                      <Button variant="contained" color="primary" onClick={() => setShowChangePlan(false)}>
                        Back
                      </Button>
                    </CardActions>
                  </div>
                ) : (
                  <div style={{ textAlign: "center", padding: "50px" }}>
                    <CircularProgress color="secondary" className="loadingCircle" />
                  </div>
                )}
              </Card>
            </div>
          ) : (
            <div style={{ paddingBottom: "20px" }}>
              <h2
                className="userCard"
                role="button"
                tabIndex={0}
                onClick={() => setShowClientInfo((prev) => !prev)}
              >
                Organization Info
              </h2>
              {showClientInfo && (
                <Card className="cardStyle">
                  {ClientInfo ? (
                    <div>
                      <CardContent>
                        <div>
                          <div>
                            Organization: <b className="bolder">{ClientInfo[0].ClientName}</b>
                          </div>
                          <div>
                            Address: <b className="bolder">{ClientInfo[0].AddressLine1}</b>
                          </div>
                          <div>
                            <b className="bolder">
                              {ClientInfo[0].City}, {ClientInfo[0].StateCode} {ClientInfo[0].ZipCode}
                            </b>
                          </div>
                          <div>
                            Contact:{" "}
                            <b className="bolder">
                              {ClientInfo[0].ClientContactFirstName}{" "}
                              {ClientInfo[0].ClientContactLastName}
                            </b>
                          </div>
                          <div>
                            Contact Email:{" "}
                            <b className="bolder">{ClientInfo[0].ClientContactEmailAddress}</b>
                          </div>
                          <div>
                            Plan:{" "}
                            <b className="bolder">
                              {PlanLookup[ClientInfo[0].ClientPlanID].title}
                            </b>
                          </div>
                          <div>
                            Description:{" "}
                            <b className="bolder">
                              {PlanLookup[ClientInfo[0].ClientPlanID].Description}
                            </b>
                          </div>
                        </div>
                      </CardContent>
                      {user.RoleID === 1 && (
                        <CardActions style={{ justifyContent: "space-between" }}>
                          <Button
                            variant="contained"
                            color="primary"
                            onClick={() => setEditClient(true)}
                          >
                            Edit
                          </Button>
                          <Button
                            variant="contained"
                            color="secondary"
                            onClick={() => setShowChangePlan(true)}
                          >
                            Change Plan
                          </Button>
                        </CardActions>
                      )}
                    </div>
                  ) : (
                    <div style={{ textAlign: "center", padding: "50px" }}>
                      <CircularProgress color="secondary" className="loadingCircle" />
                    </div>
                  )}
                </Card>
              )}
            </div>
          )}
        </div>

        {/* Invite User */}
        {user.RoleID === 1 &&
          !InvitingUser && (
            <div style={{ textAlign: "center" }}>
              <Button
                variant="contained"
                style={{ width: "42%" }}
                color="primary"
                onClick={() => setInvitingUser(true)}
              >
                Invite User
              </Button>
            </div>
          )}

        {user.RoleID === 1 && InvitingUser && (
          <div className="inviteUser">
            <h2 className="userCard">Invite User</h2>
            <Card className="cardStyle">
              {ClientInfo ? (
                <div>
                  <CardContent>
                    <form>
                      <TextField
                        label="First Name"
                        className="inviteUserInput"
                        name="first"
                        variant="outlined"
                        onChange={changeValue}
                        required
                        InputLabelProps={getStandardInputLabelProps()}
                      />
                      <TextField
                        label="Last Name"
                        className="inviteUserInput"
                        name="last"
                        variant="outlined"
                        onChange={changeValue}
                        required
                        InputLabelProps={getStandardInputLabelProps()}
                      />
                      <TextField
                        label="Email"
                        className="inviteUserInput"
                        name="Email"
                        variant="outlined"
                        onChange={changeValue}
                        required
                        InputLabelProps={getStandardInputLabelProps()}
                      />
                      <TextField
                        label="Title"
                        className="inviteUserInput"
                        name="Title"
                        variant="outlined"
                        onChange={changeValue}
                        required
                        InputLabelProps={getStandardInputLabelProps()}
                      />
                      <TextField
                        id="Role"
                        select
                        label="Role"
                        variant="outlined"
                        name="RoleID"
                        required
                        className="without-padding"
                        InputLabelProps={getStandardInputLabelProps()}
                        InputProps={{ style: { minWidth: "245px", marginBottom: "10px" } }}
                        SelectProps={{
                          MenuProps: {
                            anchorOrigin: { vertical: "bottom", horizontal: "left" },
                            getContentAnchorEl: null,
                          },
                        }}
                        onChange={changeValue}
                      >
                        <MenuItem value="" hidden />
                        <MenuItem value={1}>Admin</MenuItem>
                        <MenuItem value={2}>User</MenuItem>
                      </TextField>
                      <TextField
                        id="UserLevel"
                        select
                        name="UserLevel"
                        label="UserLevel"
                        variant="outlined"
                        required
                        className="without-padding"
                        InputLabelProps={getStandardInputLabelProps()}
                        InputProps={{ style: { minWidth: "245px", marginBottom: "10px" } }}
                        SelectProps={{
                          MenuProps: {
                            anchorOrigin: { vertical: "bottom", horizontal: "left" },
                            getContentAnchorEl: null,
                          },
                        }}
                        onChange={changeValue}
                      >
                        <MenuItem value={1}>Maintainer</MenuItem>
                        <MenuItem value={2}>Reader</MenuItem>
                      </TextField>

                      {inviteError && (
                        <p style={{ color: "red" }}>
                          Invite Failed. Possible causes: network failure or email already exists.
                        </p>
                      )}
                    </form>
                  </CardContent>
                  <CardActions style={{ justifyContent: "space-between" }}>
                    <Button variant="contained" color="primary" onClick={submitForm}>
                      Submit
                    </Button>
                    <Button
                      variant="contained"
                      color="secondary"
                      onClick={() => setInvitingUser(false)}
                    >
                      Cancel
                    </Button>
                  </CardActions>
                </div>
              ) : (
                <div style={{ textAlign: "center", padding: "50px" }}>
                  <CircularProgress color="secondary" className="loadingCircle" />
                </div>
              )}
            </Card>
          </div>
        )}

        {/* Users Table */}
        {people && (
          <div>
            <h2
              className="userTable"
              role="button"
              tabIndex={0}
              onClick={() => setShowUserTable((prev) => !prev)}
            >
              {`Users (${people.length})`}
            </h2>
          </div>
        )}

        {people && showUserTable && (
          <TableContainer component={Paper} className="tableShadow" style={{ margin: "auto" }}>
            {people.length !== 0 || isLoadingUser ? (
              <Table aria-label="simple table">
                <TableHead>
                  <TableRow>
                    <TableCell>First</TableCell>
                    <TableCell>Last</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>RoleID</TableCell>
                    <TableCell>User Level</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {people.map((row) => (
                    <TableRow key={row.LoginID}>
                      <TableCell>{row.FirstName}</TableCell>
                      <TableCell>{row.LastName}</TableCell>
                      <TableCell>{row.EmailAddress}</TableCell>
                      <TableCell>{row.RoleID === 1 ? "Admin" : "User"}</TableCell>
                      <TableCell>{row.UserLevel}</TableCell>
                      <TableCell>{row.IsActive ? "Active" : "Not Registered"}</TableCell>
                      <TableCell>
                        {row.IsActive ? (
                          // Only show delete button for admin users
                          user.RoleID === 1 ? (
                            <Button
                              variant="contained"
                              color="secondary"
                              onClick={() => deleteUser(row)}
                            >
                              Delete
                            </Button>
                          ) : (
                            <span style={{ color: '#666', fontSize: '12px' }}>
                              Admin only
                            </span>
                          )
                        ) : (
                          <Button variant="contained" color="primary">
                            Resend Invite
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <div style={{ textAlign: "center", padding: "50px" }}>
                <CircularProgress color="secondary" className="loadingCircle" />
              </div>
            )}
          </TableContainer>
        )}
      </div>
    </div>
  );
}

export default Account;
